import { useMemo, useRef } from "react";
import { type AnalyticValues, useAnalytics } from "../../../contexts/analytics/hooks";
import type { DownloadFormat } from "../../UIToolkit/Clickable/DownloadFormatButton";
import type { ScheduleExportTab } from "./consts";
import type { ScheduleExportSettingKey } from "./types";

/** Stamped on every event, so one Mixpanel breakdown covers both tabs. */
export type ScheduleExportAnalyticsProps = AnalyticValues & {
	department_id: string;
	mode: ScheduleExportTab;
};

type Params = { props: ScheduleExportAnalyticsProps };

const errorMessageOf = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * Analytics for the schedule export popup.
 *
 * Two things this centralizes:
 *
 * 1. **Legacy continuity.** Existing Mixpanel reports are built on the old event names, so the
 *    lifecycle events fire the new `schedule_export_*` name AND the legacy name for one release,
 *    carrying the legacy property spellings too (the old download-error events used `error`, not
 *    `error_message`). Keeping that table here makes the eventual cleanup a one-file change.
 * 2. **Callback identity.** `useAnalytics` returns fresh `track`/`trackError` whenever an ancestor
 *    re-renders, and these end up inside the memoized PDF build args — a changing identity there
 *    rebuilds the whole document on every render. Holding them in a ref keeps every method stable.
 */
export const useScheduleExportAnalytics = ({ props }: Params) => {
	const { track, trackError } = useAnalytics();

	const sinkRef = useRef({ track, trackError });
	sinkRef.current = { track, trackError };

	const propsRef = useRef(props);
	propsRef.current = props;

	return useMemo(() => {
		const fire = (event: string, extra?: AnalyticValues) => sinkRef.current.track(event, { ...propsRef.current, ...extra });
		const fireError = (event: string, extra?: AnalyticValues) => sinkRef.current.trackError(event, { ...propsRef.current, ...extra });
		const isWeekly = () => propsRef.current.mode === "weekly";

		return {
			trackOpened: (extra?: AnalyticValues) => {
				fire("schedule_export_opened", extra);
				fire("export_pdf_button_clicked", extra);
			},
			trackClosed: () => {
				fire("schedule_export_closed");
				fire("download_dialog_cancelled");
			},
			trackTabChanged: (mode: ScheduleExportTab) => {
				fire("schedule_export_tab_changed", { mode });
				fire("download_mode_changed", { mode });
			},
			/** Collapses the five legacy per-edge date events; none of them backed a report. */
			trackRangeChanged: (edge: "start" | "end", extra?: AnalyticValues) => fire("schedule_export_range_changed", { edge, ...extra }),
			/**
			 * Collapses the four legacy `*_toggled` / `sort_option_changed` events.
			 *
			 * `number` is in the union for the page-setup fit counts, which are whole pages rather than a
			 * toggle — the value lands in Mixpanel as-is either way.
			 */
			trackSettingChanged: (setting: ScheduleExportSettingKey, value: string | boolean | number) => fire("schedule_export_setting_changed", { setting, value }),
			/**
			 * "Set every day column to this width" — its own event rather than a `trackSettingChanged`.
			 *
			 * The width is not a `MonthlyExportSettingsValue` key, and it is a deliberate command rather
			 * than a toggle. Raw column DRAGS stay untracked (they are pointer noise); this is not one.
			 */
			trackDayColumnWidthSet: (widthPx: number) => fire("schedule_export_day_column_width_set", { width_px: widthPx }),
			/**
			 * The two department-default commands. Support-only, and each one writes to every user row in
			 * the department — so they are worth their own events rather than folding into
			 * `trackSettingChanged`, which reports one scheduler changing their own document.
			 */
			trackResetToDepartmentDefault: () => fire("schedule_export_reset_to_department_default"),
			trackSavedAsDepartmentDefault: () => fire("schedule_export_saved_as_department_default"),
			trackDepartmentDefaultError: (action: "reset" | "save", error: unknown) =>
				fireError("schedule_export_department_default_error", { action, error_message: errorMessageOf(error) }),
			trackPreviewStart: () => fire("schedule_export_preview_start"),
			trackPreviewSuccess: (extra?: AnalyticValues) => fire("schedule_export_preview_success", extra),
			trackPreviewError: (error: unknown, extra?: AnalyticValues) =>
				fireError("schedule_export_preview_error", { error_message: errorMessageOf(error), ...extra }),
			trackPtoFetchError: (error: unknown) => fireError("schedule_export_pto_fetch_error", { error_message: errorMessageOf(error) }),
			trackDownloadStart: (format: DownloadFormat) => {
				fire("schedule_export_download_start", { format });
				fire(isWeekly() ? "export_pdf_started" : "monthly_export_started", { format });
			},
			trackDownloadSuccess: (format: DownloadFormat) => {
				fire("schedule_export_download_success", { format });
				fire(isWeekly() ? "export_pdf_completed" : "monthly_export_completed", { format });
			},
			trackDownloadError: (format: DownloadFormat, error: unknown) => {
				const message = errorMessageOf(error);
				fireError("schedule_export_download_error", { format, error_message: message });
				// The legacy events spelled this property `error`; send both so old charts resolve.
				fireError(isWeekly() ? "export_pdf_error" : "monthly_export_error", { format, error: message, error_message: message });
			},
			// Print is always the PDF layout, never a format choice, so it gets its own events rather than
			// reusing `trackDownload*` with format "pdf" — conflating the two would hide how often people
			// print versus save the file.
			trackPrintStart: () => fire("schedule_export_print_start"),
			trackPrintSuccess: () => fire("schedule_export_print_success"),
			trackPrintError: (error: unknown) => fireError("schedule_export_print_error", { error_message: errorMessageOf(error) }),
		};
	}, []);
};
